import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { onLaunchAndResume, versionState, type VersionState } from '../lib/app-version.js';
import { t } from '../i18n/index.js';

/** Dismissals are remembered per build for a day: an offer, not a nag. */
const DISMISS_KEY = 'siva.updateDismissed';
const DAY_MS = 24 * 60 * 60 * 1000;

function dismissedFor(versionCode: number): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { code?: number; at?: number };
    return saved.code === versionCode && Date.now() - (saved.at ?? 0) < DAY_MS;
  } catch {
    return false;
  }
}

function rememberDismissal(versionCode: number): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ code: versionCode, at: Date.now() }));
  } catch {
    /* private mode: the offer simply comes back next time */
  }
}

/**
 * "There is a newer version of the app."
 *
 * An offer, and Later is a real answer — for a day, per build, after which it asks again. The
 * other case, a build too old to punch with, is deliberately not handled here: it belongs on the
 * punch screen, in place of the button that will not work, so that hours, weekly off and payment
 * advices stay reachable for somebody who cannot update this minute.
 *
 * Invisible in every browser, on every iPhone, and on a build that is already current.
 */
export function UpdatePrompt() {
  const [state, setState] = useState<VersionState | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(
    () =>
      onLaunchAndResume(() => {
        void versionState().then(setState);
      }),
    [],
  );

  if (!state || state.standing !== 'update_available' || !state.info?.latest) return null;
  const latest = state.info.latest;
  if (dismissed || dismissedFor(latest.version_code)) return null;

  return (
    <div className="update-card">
      <Download size={22} aria-hidden="true" />
      <div>
        <h3>{t('update_available_title')}</h3>
        <p className="muted">
          {t('update_available_body', { version: latest.version_name })}
          {state.fromPlay ? ` ${t('update_via_play')}` : ''}
        </p>
        {latest.notes && <p className="muted small-text">{latest.notes}</p>}
        <div className="row">
          {state.updateUrl && (
            <a className="button" href={state.updateUrl} rel="noreferrer">
              {state.fromPlay ? t('update_open_play') : t('update_download')}
            </a>
          )}
          <button
            type="button"
            className="secondary"
            onClick={() => {
              rememberDismissal(latest.version_code);
              setDismissed(true);
            }}
          >
            {t('update_later')}
          </button>
        </div>
      </div>
    </div>
  );
}
